import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { prism } from "react-syntax-highlighter/dist/esm/styles/prism";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import Layout from "../../components/Layout";
import { useAuth } from "../../contexts/AuthContext";
import config from "../../config";
import plusIcon from "../../public/img/plus.svg";
import arrow from "../../public/img/arrow.svg";
import copyIcon from "../../public/img/copy.svg";
import ltLogo from "../../public/img/LT_logo.svg";
import historyIconDark from "../../public/img/history_icon_dark.svg";
import openaiLogo from "../../public/img/openai_logo_dark.svg";
import "./PersonaChat.css";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Typography from "@tiptap/extension-typography";
import { Markdown } from "tiptap-markdown";
import UserSelector from "../../components/UserSelector";

// Helper to construct WebSocket URL for persona chat
const getWebSocketURL = () => {
  const baseUrl = config.api.baseUrl;
  let wsUrl = baseUrl.replace(/^http/, "ws");
  wsUrl += "/synthetic-audience/personas/chat";  // Persona-specific endpoint
  return wsUrl;
};

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const PersonaChat = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Parse URL parameters - using persona ID from route params
  const pathParts = location.pathname.split('/');
  const personaId = pathParts[pathParts.indexOf('personas') + 1];

  const searchParams = new URLSearchParams(location.search);
  const historyIdParam = searchParams.get("historyId");
  const messageTimestampParam = searchParams.get("messageTimestamp");
  const isComingFromSearch = !!(historyIdParam && messageTimestampParam);
  
  const [chatbot, setChatbot] = useState(null);
  const [loadingChatbot, setLoadingChatbot] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [, setInputText] = useState("");
  const [messages, setMessages] = useState([]);
  // const [isTyping, setIsTyping] = useState(false); // Kept for potential future use, but display is removed
  
  // Theme state for code blocks
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  
  // Message pagination state
  // eslint-disable-next-line no-unused-vars
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const loadingOlderMessagesRef = useRef(false);
  const adjustingScrollRef = useRef(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [, setTotalMessageCount] = useState(0);
  const messagesContainerRef = useRef(null);

  const wsInstance = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [, setCurrentFunctionCall] = useState(null);
  const [, setCurrentWebSearch] = useState(null);
  const [, setCurrentCodeInterpreter] = useState(null);
  const [isResponding, setIsResponding] = useState(false); // Track if chatbot is responding
  const [copiedMessageId, setCopiedMessageId] = useState(null); // Track which message was copied
  const [editingMessageId, setEditingMessageId] = useState(null); // Track which message is being edited
  const [editedContent, setEditedContent] = useState(""); // Store edited content
  const [expandedPrompts, setExpandedPrompts] = useState(new Set()); // Track which revised prompts are expanded
  // TipTap Editor initialization
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: true,
        orderedList: true,
        heading: true,
        codeBlock: true,
      }),
      Underline,
      Link.configure({ openOnClick: false }),
      Typography,
      Placeholder.configure({
        placeholder: "Say anything...",
      }),
      Markdown.configure({
        html: false,
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "chat-input tiptap-editor",
      },
      handleKeyDown(view, event) {
        // Cmd/Ctrl+Enter -> send
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
          event.preventDefault();
          handleSendFromEditor();
          return true;
        }

        // Shift+Enter -> context-aware behavior
        // - Inside lists or code blocks: insert a hard break within the block
        // - Inside a heading: split to a new paragraph
        // - Otherwise: split to a new paragraph
        if (event.shiftKey && event.key === "Enter") {
          event.preventDefault();
          const inList =
            editor?.isActive?.("bulletList") ||
            editor?.isActive?.("orderedList");
          const inCode = editor?.isActive?.("codeBlock");

          if (inList || inCode) {
            if (editor?.chain) {
              editor.chain().focus().setHardBreak().run();
              return true;
            }
            return false;
          }

          if (editor?.chain) {
            editor.chain().focus().splitBlock().setParagraph().run();
            return true;
          }
          return false;
        }

        // Enter (no Shift) -> send, except when inside list/code blocks
        if (event.key === "Enter" && !event.shiftKey) {
          if (
            editor?.isActive?.("bulletList") ||
            editor?.isActive?.("orderedList") ||
            editor?.isActive?.("codeBlock")
          ) {
            return false; // allow default behavior for lists/code
          }
          if (!isConnected) return true;
          event.preventDefault();
          handleSendFromEditor();
          return true;
        }

        return false;
      },
    },
  });

  // Track editor content to enable/disable send button reliably
  const [editorHasContent, setEditorHasContent] = useState(false);

  useEffect(() => {
    if (!editor) return;
    const update = () => {
      try {
        const txt = editor.getText ? editor.getText().trim() : "";
        setEditorHasContent(txt.length > 0);
      } catch (e) {
        setEditorHasContent(false);
      }
    };
    editor.on("update", update);
    editor.on("selectionUpdate", update);
    update();
    return () => {
      try {
        editor.off("update", update);
        editor.off("selectionUpdate", update);
      } catch (e) {}
    };
  }, [editor]);

  const getEditorMarkdown = () => {
    if (!editor) return "";
    try {
      // Prefer Markdown serialization from the extension if available
      const md = editor?.storage?.markdown?.getMarkdown
        ? editor.storage.markdown.getMarkdown()
        : null;
      if (md != null) return md;
      return editor.getText();
    } catch (e) {
      return editor.getText();
    }
  };

  const clearEditor = () => {
    if (editor) editor.commands.clearContent(true);
  };


  const messagesEndRef = useRef(null);
  const prevChatbotIdRef = useRef(null);
  const currentBotMessageId = useRef(null);
  const fileInputRef = useRef(null); // For handling file input clicks
  const [attachedFiles, setAttachedFiles] = useState([]); // To hold file objects for submission
  const [attachmentPreviews, setAttachmentPreviews] = useState([]); // For rendering previews with object URLs
  const [isDraggingFile, setIsDraggingFile] = useState(false); // Track if user is dragging files over the page

  // Chat history sidebar state
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyCards, setHistoryCards] = useState([]);
  const [loadingHistoryCards, setLoadingHistoryCards] = useState(false);
  const [loadingMoreHistory] = useState(false);
  const loadingMoreHistoryRef = useRef(false); // Immediate check to prevent race conditions
  const [historyError, setHistoryError] = useState(null);
  const [currentHistoryId, setCurrentHistoryId] = useState(null);
  const currentHistoryIdRef = useRef(null);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const historyListRef = useRef(null);
  const [loadingFromSearch, setLoadingFromSearch] = useState(isComingFromSearch);
  const [targetMessageTimestamp, setTargetMessageTimestamp] = useState(messageTimestampParam || null);
  const messageRefs = useRef({});
  
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    cardId: null,
  });
  const [shareMenu, setShareMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    cardId: null,
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState("");
  const [webSearchEnabled] = useState(true);

  // Format stored UTC ISO into local display by subtracting 7 hours first, then formatting
  // MM/DD/YYYY h:mm:ss AM/PM (no TZ suffix). This matches MST conversion per your requirement.
  const formatMSTFromUTC = (isoString) => {
    try {
      const parsed = new Date(isoString);
      if (isNaN(parsed.getTime())) return "";
      // subtract 7 hours from the parsed UTC value
      const adjusted = new Date(parsed.getTime() - 7 * 60 * 60 * 1000);
      // format in user's current local timezone
      return adjusted.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return "";
    }
  };

  // Format history card time: if locally generated, show local time directly; else convert from UTC to MST
  const formatHistoryCardTime = (card) => {
    const ts = card?.updated_at;
    if (!ts) return "";
    if (card?.fromLocal) {
      try {
        return new Date(ts).toLocaleString("en-US", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      } catch (e) {
        return "";
      }
    }
    return formatMSTFromUTC(ts);
  };

  // Effect for cleaning up object URLs on unmount
  useEffect(() => {
    return () => {
      // When Chat component unmounts, revoke any lingering URLs to prevent memory leaks
      attachmentPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // IMPORTANT: Empty dependency array means this runs only on mount and cleanup on unmount.

  // New component for styled code blocks with header and copy button
  const StyledCodeBlock = ({ language, children }) => {
    const [copySuccess, setCopySuccess] = useState("");
    const [showPreview, setShowPreview] = useState(false); // New state for HTML preview
    const codeString = String(children).replace(/\n$/, "");

    const isHtml = language?.toLowerCase() === "html";

    const handleCopy = () => {
      navigator.clipboard.writeText(codeString).then(
        () => {
          setCopySuccess("Copied!");
          setTimeout(() => setCopySuccess(""), 2000);
        },
        () => {
          setCopySuccess("Failed");
          setTimeout(() => setCopySuccess(""), 2000);
        }
      );
    };

    const togglePreview = () => {
      setShowPreview(!showPreview);
    };

    return (
      <div className="styled-code-block-container">
        <div className="styled-code-block-header">
          <span className="styled-code-block-language">
            {language || "code"}
          </span>
          <div className="styled-code-block-header-buttons">
            {isHtml && (
              <button
                onClick={togglePreview}
                className="styled-code-block-toggle-button"
                title={showPreview ? "Show Code" : "Preview HTML"}
              >
                {showPreview ? "Show Code" : "Preview HTML"}
              </button>
            )}
            <span
              onClick={handleCopy}
              className="styled-code-block-copy-button"
              title={copySuccess || "Copy code"}
              role="button"
              tabIndex={0}
              onKeyDown={(e) =>
                (e.key === "Enter" || e.key === " ") && handleCopy()
              }
            >
              <img src={copyIcon} alt="Copy code" className="copy-icon-svg" />
            </span>
          </div>
        </div>
        {isHtml && showPreview ? (
          <iframe
            className="styled-code-block-html-preview"
            srcDoc={codeString}
            sandbox="allow-scripts"
            title="HTML Preview"
          />
        ) : (
          <SyntaxHighlighter
            style={isDarkMode ? vscDarkPlus : prism}
            language={language}
            // PreTag="div" // Keeping this commented, <pre> is better for themes
            customStyle={{ margin: 0, borderRadius: "0 0 6px 6px" }}
          >
            {codeString}
          </SyntaxHighlighter>
        )}
      </div>
    );
  };

  const getUserInitials = () => {
    if (!user) return "?";
    let initials = "";
    if (user.first_name) initials += user.first_name.charAt(0).toUpperCase();
    if (user.last_name) initials += user.last_name.charAt(0).toUpperCase();
    return initials || user.email?.charAt(0).toUpperCase() || "?";
  };

  const getChatbotVisual = (type = "any") => {
    if (!chatbot) return null;
    if (chatbot.logo && (type === "logo" || type === "any"))
      return chatbot.logo;
    if (chatbot.icon && (type === "icon" || type === "any"))
      return chatbot.icon;
    return null;
  };

  const scrollToBottom = useCallback((instant = false) => {
    if (instant) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Track previous message count to detect new messages vs history loads
  const prevMessageCountRef = useRef(0);

  // Use layoutEffect for initial scroll to prevent visible jump
  useLayoutEffect(() => {
    const currentCount = messages.length;
    const prevCount = prevMessageCountRef.current;

    if (currentCount === 0) {
      prevMessageCountRef.current = 0;
      return;
    }

    // Check if we're loading older messages or adjusting scroll - don't interfere
    if (loadingOlderMessagesRef.current || adjustingScrollRef.current) {
      prevMessageCountRef.current = currentCount;
      return;
    }

    // If this is the first load of messages from history, scroll instantly to bottom
    // Using layoutEffect ensures this happens before paint, preventing visible jump
    if (prevCount === 0 && currentCount > 0 && messages[0]?.fromHistory) {
      scrollToBottom(true);
      prevMessageCountRef.current = currentCount;
      return;
    }

    prevMessageCountRef.current = currentCount;
  }, [messages, scrollToBottom]);

  // Separate effect for smooth scrolling on new messages
  useEffect(() => {
    const currentCount = messages.length;
    const prevCount = prevMessageCountRef.current;

    if (currentCount === 0 || loadingOlderMessagesRef.current || adjustingScrollRef.current) {
      return;
    }

    // Only auto-scroll smoothly when new messages are added (not prepended older ones)
    // New messages get added to the end, so we check if the last message is new
    if (currentCount > prevCount && !messages[messages.length - 1]?.fromHistory) {
      scrollToBottom(false);
    }
  }, [messages, scrollToBottom]);

  // Watch for theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const currentTheme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(currentTheme === 'dark');
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!personaId) {
      if (
        wsInstance.current &&
        wsInstance.current.readyState === WebSocket.OPEN
      ) {
        wsInstance.current.close(1000, "Chatbot ID cleared");
      }
      setMessages([]);
      setHasMoreMessages(false);
      setTotalMessageCount(0);
      setChatbot(null);
      setIsConnected(false);
      setIsConnecting(false);
      setChatError(null);
      setHistoryCards([]);
      setHasMoreHistory(false);
      setCurrentHistoryId(null);
      return;
    }

    // Wait for chatbot data to be loaded before connecting WebSocket
    if (!chatbot || loadingChatbot) {
      return;
    }

    if (prevChatbotIdRef.current !== personaId && wsInstance.current) {
      wsInstance.current.onclose = null;
      wsInstance.current.close(1000, "Chatbot changed");
    }
    prevChatbotIdRef.current = personaId;

    // Only clear messages if we're not loading a specific history from search
    // If coming from search, the history loading effect will set the messages
    if (!historyIdParam) {
      setMessages([]);
      setHasMoreMessages(false);
      setTotalMessageCount(0);
    }
    setChatError(null);
    setIsConnecting(true);
    setIsConnected(false);
    currentBotMessageId.current = null;
    // setIsTyping(false); // Display removed

    const ws = new WebSocket(getWebSocketURL(chatbot));
    wsInstance.current = ws;

    ws.onopen = () => {
      setIsConnecting(false);
      setIsConnected(true);
      setChatError(null);
      const initPayload = { type: "init", persona_id: personaId };

      // Check if we have a chat ID from the current ref or from URL (search)
      if (currentHistoryIdRef.current) {
        initPayload.chat_id = currentHistoryIdRef.current;
      } else if (historyIdParam) {
        // If coming from search, use the history ID from URL
        initPayload.chat_id = historyIdParam;
      }

      ws.send(JSON.stringify(initPayload));
    };

    ws.onmessage = (event) => {
      let serverMessage;
      try {
        serverMessage = JSON.parse(event.data);
      } catch (e) {
        setChatError("Received an invalid message from the server.");
        return;
      }

      // Debug logging for web search events
      if (
        serverMessage.type === "web_search_started" ||
        serverMessage.type === "web_search_completed" ||
        serverMessage.type === "image_generation_started" ||
        serverMessage.type === "image_generation_completed"
      ) {
        console.log(
          "WebSocket message received:",
          serverMessage.type,
          serverMessage
        );
      }

      switch (serverMessage.type) {
        case "function_call_started":
          // Replace typing indicator with a status box for function call
          setCurrentFunctionCall({
            name: serverMessage.function_name || "Function",
            callId: serverMessage.call_id,
            responseId: serverMessage.response_id,
            active: true,
          });
          setMessages((prev) => {
            if (!currentBotMessageId.current) return prev;
            return prev.map((m) =>
              m.id === currentBotMessageId.current
                ? {
                    ...m,
                    isTypingPlaceholder: false,
                    functionCallStatus: "started",
                    functionName: serverMessage.function_name,
                  }
                : m
            );
          });
          break;

        case "function_call_completed":
          setCurrentFunctionCall(null);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === currentBotMessageId.current
                ? { ...m, functionCallStatus: "completed" }
                : m
            )
          );
          break;

        case "code_interpreter_started":
          console.log(
            "Code interpreter started event received:",
            serverMessage
          );
          setCurrentCodeInterpreter({
            callId: serverMessage.call_id,
            responseId: serverMessage.response_id,
            active: true,
          });
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) return prev;

            const messageExists = prev.some((m) => m.id === responseId);

            if (messageExists) {
              return prev.map((m) =>
                m.id === responseId
                  ? {
                      ...m,
                      isTypingPlaceholder: false,
                      codeInterpreterStatus: "started",
                    }
                  : m
              );
            } else {
              const newMessage = {
                id: responseId,
                role: "assistant",
                content: "",
                timestamp: new Date().toISOString(),
                isTypingPlaceholder: false,
                codeInterpreterStatus: "started",
              };
              return [...prev, newMessage];
            }
          });
          if (!currentBotMessageId.current && serverMessage.response_id) {
            currentBotMessageId.current = serverMessage.response_id;
          }
          break;

        case "code_interpreter_completed":
          setCurrentCodeInterpreter(null);
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) return prev;

            // Update message to completed
            const updated = prev.map((m) =>
              m.id === responseId
                ? {
                    ...m,
                    codeInterpreterStatus: "completed",
                    isTypingPlaceholder: true, // Resume typing/loading if text follows
                  }
                : m
            );
            return updated;
          });
          break;

        case "web_search_started":
          // Show web search status similar to function calls
          console.log("Web search started event received:", serverMessage);
          setCurrentWebSearch({
            callId: serverMessage.web_search_call_id,
            responseId: serverMessage.response_id,
            action: serverMessage.action,
            active: true,
          });
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) {
              console.warn("No response ID for web_search_started");
              return prev;
            }

            // Check if message already exists
            const messageExists = prev.some((m) => m.id === responseId);
            console.log(`Message exists for ${responseId}:`, messageExists);

            if (messageExists) {
              // Update existing message - force web search status to show
              const updated = prev.map((m) =>
                m.id === responseId
                  ? {
                      ...m,
                      isTypingPlaceholder: false, // Force false to show web search status
                      webSearchStatus: "started",
                      webSearchAction: serverMessage.action,
                    }
                  : m
              );
              console.log(
                "Updated message with web search status:",
                updated.find((m) => m.id === responseId)
              );
              return updated;
            } else {
              // Create new message if it doesn't exist yet
              const newMessage = {
                id: responseId,
                role: "assistant",
                content: "",
                timestamp: new Date().toISOString(),
                isTypingPlaceholder: false, // Don't show loading dots, show web search status
                webSearchStatus: "started",
                webSearchAction: serverMessage.action,
              };
              console.log(
                "Created new message with web search status:",
                newMessage
              );
              return [...prev, newMessage];
            }
          });
          // Update currentBotMessageId if it wasn't set
          if (!currentBotMessageId.current && serverMessage.response_id) {
            currentBotMessageId.current = serverMessage.response_id;
          }
          break;

        case "web_search_completed":
          setCurrentWebSearch(null);
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) return prev;

            // Update web search message to completed
            const updated = prev.map((m) =>
              m.id === responseId
                ? {
                    ...m,
                    webSearchStatus: "completed",
                  }
                : m
            );

            // Create a new message for the chatbot's response with loading dots
            const newResponseId = `${responseId}_response`;
            const responseMessageExists = updated.some(
              (m) => m.id === newResponseId
            );

            if (!responseMessageExists) {
              // Create new response message with loading dots
              currentBotMessageId.current = newResponseId;
              return [
                ...updated,
                {
                  id: newResponseId,
                  role: "assistant",
                  content: "",
                  timestamp: new Date().toISOString(),
                  isTypingPlaceholder: true, // Show loading dots
                },
              ];
            }

            return updated;
          });
          break;

        case "image_generation_started":
          console.log(
            "*** FRONTEND: image_generation_started received ***",
            serverMessage
          );
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) {
              console.warn(
                "*** FRONTEND: No response ID for image_generation_started ***",
                serverMessage
              );
              return prev;
            }

            console.log(
              "*** FRONTEND: Processing image_generation_started for responseId ***",
              responseId
            );

            // Check if message already exists
            const messageExists = prev.some((m) => m.id === responseId);
            console.log("*** FRONTEND: Message exists? ***", messageExists);

            if (messageExists) {
              // Update existing message
              console.log("*** FRONTEND: Updating existing message ***");
              return prev.map((m) =>
                m.id === responseId
                  ? {
                      ...m,
                      isTypingPlaceholder: false,
                      imageGenerationStatus: "started",
                    }
                  : m
              );
            } else {
              // Create new message
              console.log("*** FRONTEND: Creating new message ***");
              const newMessage = {
                id: responseId,
                role: "assistant",
                content: "",
                timestamp: new Date().toISOString(),
                isTypingPlaceholder: false,
                imageGenerationStatus: "started",
              };
              return [...prev, newMessage];
            }
          });
          break;

        case "image_generation_completed":
          console.log("*** FRONTEND: image_generation_completed received ***", {
            ...serverMessage,
            image_base64: serverMessage.image_base64
              ? `<base64_data_length:${serverMessage.image_base64.length}>`
              : null,
          });
          setMessages((prev) => {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) {
              console.warn(
                "*** FRONTEND: No response ID for image_generation_completed ***",
                serverMessage
              );
              return prev;
            }

            console.log(
              "*** FRONTEND: Processing image_generation_completed for responseId ***",
              responseId
            );
            console.log(
              "*** FRONTEND: Has image_path? ***",
              !!serverMessage.image_path
            );
            console.log(
              "*** FRONTEND: image_path value ***",
              serverMessage.image_path
            );

            // Update message with generated image
            const updated = prev.map((m) => {
              if (m.id === responseId) {
                let imageUrl = null;

                // Check if we have image_path (filename)
                if (serverMessage.image_path) {
                  // Construct URL to fetch image from server
                  imageUrl = `${config.api.baseUrl}${
                    config.api.endpoints.chatbot.base
                  }/files/${encodeURIComponent(serverMessage.image_path)}`;
                  console.log("*** FRONTEND: Created image URL from path ***", {
                    imagePath: serverMessage.image_path,
                    imageUrl: imageUrl,
                  });
                } else {
                  console.warn(
                    "*** FRONTEND: No image_path in serverMessage ***",
                    {
                      serverMessageKeys: Object.keys(serverMessage),
                      hasImagePath: "image_path" in serverMessage,
                    }
                  );
                }

                console.log("*** FRONTEND: Updating message with image ***", {
                  messageId: m.id,
                  hasImageUrl: !!imageUrl,
                  imageUrl: imageUrl,
                  revisedPrompt: serverMessage.revised_prompt,
                });

                const updatedMessage = {
                  ...m,
                  imageGenerationStatus: "completed",
                  generatedImage: imageUrl, // Store URL instead of data URL
                  revisedPrompt: serverMessage.revised_prompt,
                  isTypingPlaceholder: false, // Explicitly clear loading dots
                };

                console.log("*** FRONTEND: Updated message state ***", {
                  id: updatedMessage.id,
                  imageGenerationStatus: updatedMessage.imageGenerationStatus,
                  hasGeneratedImage: !!updatedMessage.generatedImage,
                  generatedImageType: typeof updatedMessage.generatedImage,
                  generatedImageUrl: updatedMessage.generatedImage,
                  isTypingPlaceholder: updatedMessage.isTypingPlaceholder,
                });

                return updatedMessage;
              }
              return m;
            });

            const updatedMessage = updated.find((m) => m.id === responseId);
            console.log("*** FRONTEND: Updated messages ***", updatedMessage);
            console.log("*** FRONTEND: Updated message state ***", {
              id: updatedMessage?.id,
              imageGenerationStatus: updatedMessage?.imageGenerationStatus,
              hasGeneratedImage: !!updatedMessage?.generatedImage,
              isTypingPlaceholder: updatedMessage?.isTypingPlaceholder,
              content: updatedMessage?.content,
            });
            return updated;
          });
          break;

        case "init_success":
          setChatError(null);
          break;

        case "history_opened":
          if (serverMessage.chat_history_id) {
            setCurrentHistoryId(serverMessage.chat_history_id);
            currentHistoryIdRef.current = serverMessage.chat_history_id;
            if (serverMessage.title) {
              // If the server provides initial title like "New Chat", add/update the card optimistically
              setHistoryCards((prev) => {
                const exists = prev.some(
                  (c) => c.id === serverMessage.chat_history_id
                );
                // Use current UTC timestamp; formatting logic handles display
                const updatedAt = new Date().toISOString();
                const newCard = {
                  id: serverMessage.chat_history_id,
                  title: serverMessage.title,
                  created_at: updatedAt,
                  updated_at: updatedAt,
                  fromLocal: true,
                  message_count: 1,
                };
                return exists
                  ? prev.map((c) =>
                      c.id === newCard.id ? { ...c, title: newCard.title } : c
                    )
                  : [newCard, ...prev];
              });
            }
          }
          break;
        case "history_title_updated":
          if (serverMessage.chat_history_id && serverMessage.title) {
            setHistoryCards((prev) =>
              prev.map((c) =>
                c.id === serverMessage.chat_history_id
                  ? {
                      ...c,
                      title: serverMessage.title,
                      updated_at: new Date().toISOString(),
                    }
                  : c
              )
            );
          }
          break;

        case "response_created":
          currentBotMessageId.current = serverMessage.response_id;
          setIsResponding(true); // Mark that chatbot is responding
          setMessages((prevMessages) => {
            // Check if message already exists (e.g., from web_search_started)
            const existingMessage = prevMessages.find(
              (m) => m.id === serverMessage.response_id
            );
            if (existingMessage) {
              // If the existing message has web search status, don't update it
              // Create a new message for the actual response instead
              if (
                existingMessage.webSearchStatus === "started" ||
                existingMessage.webSearchStatus === "completed"
              ) {
                // Create a new message for the chatbot's response
                // Use a modified ID to ensure it's separate
                const newResponseId = `${serverMessage.response_id}_response`;
                currentBotMessageId.current = newResponseId;
                return [
                  ...prevMessages,
                  {
                    id: newResponseId,
                    role: "assistant",
                    content: "",
                    timestamp: new Date().toISOString(),
                    isTypingPlaceholder: true,
                  },
                ];
              } else if (
                existingMessage.imageGenerationStatus === "completed"
              ) {
                // If image generation is already completed, create a separate message for text response
                // This allows the AI to add commentary or explanation after generating the image
                const newResponseId = `${serverMessage.response_id}_response`;
                currentBotMessageId.current = newResponseId;
                return [
                  ...prevMessages,
                  {
                    id: newResponseId,
                    role: "assistant",
                    content: "",
                    timestamp: new Date().toISOString(),
                    isTypingPlaceholder: true,
                  },
                ];
              } else {
                // Update existing message if it doesn't have web search or image generation status
                return prevMessages.map((m) =>
                  m.id === serverMessage.response_id
                    ? {
                        ...m,
                        role: "assistant",
                        timestamp: m.timestamp || new Date().toISOString(),
                        isTypingPlaceholder: true,
                      }
                    : m
                );
              }
            } else {
              // Create new message
              return [
                ...prevMessages,
                {
                  id: serverMessage.response_id,
                  role: "assistant",
                  content: "",
                  timestamp: new Date().toISOString(),
                  isTypingPlaceholder: true,
                },
              ];
            }
          });
          break;

        case "text_delta":
          if (serverMessage.delta) {
            const responseId =
              serverMessage.response_id || currentBotMessageId.current;
            if (!responseId) break;

            setMessages((prevMessages) => {
              // Find the message with this response_id
              const messageWithResponseId = prevMessages.find(
                (m) => m.id === responseId
              );

              // If the message has image generation completed, create/update a separate response message for text
              if (
                messageWithResponseId &&
                messageWithResponseId.imageGenerationStatus === "completed"
              ) {
                // Find or create the response message (separate from image generation message)
                const newResponseId = `${responseId}_response`;
                const responseMessage = prevMessages.find(
                  (m) => m.id === newResponseId
                );

                if (responseMessage) {
                  // Update the existing response message
                  currentBotMessageId.current = newResponseId;
                  return prevMessages.map((msg) =>
                    msg.id === newResponseId
                      ? {
                          ...msg,
                          content: (msg.content || "") + serverMessage.delta,
                          isTypingPlaceholder: false,
                        }
                      : msg
                  );
                } else {
                  // Create new response message for the chatbot's text commentary
                  currentBotMessageId.current = newResponseId;
                  return [
                    ...prevMessages,
                    {
                      id: newResponseId,
                      role: "assistant",
                      content: serverMessage.delta,
                      timestamp: new Date().toISOString(),
                      isTypingPlaceholder: false,
                    },
                  ];
                }
              }

              // If the message has web search status, create/update a separate response message
              if (
                messageWithResponseId &&
                (messageWithResponseId.webSearchStatus === "started" ||
                  messageWithResponseId.webSearchStatus === "completed")
              ) {
                // Find or create the response message (separate from web search message)
                const newResponseId = `${responseId}_response`;
                const responseMessage = prevMessages.find(
                  (m) => m.id === newResponseId
                );

                if (responseMessage) {
                  // Update the existing response message
                  currentBotMessageId.current = newResponseId;
                  return prevMessages.map((msg) =>
                    msg.id === newResponseId
                      ? {
                          ...msg,
                          content: (msg.content || "") + serverMessage.delta,
                          isTypingPlaceholder: false,
                        }
                      : msg
                  );
                } else {
                  // Create new response message for the chatbot's actual response
                  currentBotMessageId.current = newResponseId;
                  return [
                    ...prevMessages,
                    {
                      id: newResponseId,
                      role: "assistant",
                      content: serverMessage.delta,
                      timestamp: new Date().toISOString(),
                      isTypingPlaceholder: false,
                    },
                  ];
                }
              } else {
                // Normal update - no web search status, update the message directly
                if (!currentBotMessageId.current) {
                  currentBotMessageId.current = responseId;
                }
                return prevMessages.map((msg) =>
                  msg.id === responseId
                    ? {
                        ...msg,
                        content: (msg.content || "") + serverMessage.delta,
                        isTypingPlaceholder: false,
                      }
                    : msg
                );
              }
            });
          }
          break;

        case "content_update":
          // Update message content when backend replaces links (e.g., /mnt/data -> /chatbot/files)
          const updateResponseId =
            serverMessage.response_id || currentBotMessageId.current;
          if (updateResponseId && serverMessage.content) {
            setMessages((prevMessages) => {
              // Check if this is a split response (e.g., for web search or image generation)
              const splitResponseId = `${updateResponseId}_response`;
              const hasSplitResponse = prevMessages.some(
                (m) => m.id === splitResponseId
              );

              return prevMessages.map((msg) => {
                // Update the split response if it exists, otherwise update the main message
                const targetId = hasSplitResponse
                  ? splitResponseId
                  : updateResponseId;
                return msg.id === targetId
                  ? {
                      ...msg,
                      content: serverMessage.content,
                    }
                  : msg;
              });
            });
          }
          break;

        case "response_completed":
          const responseId = serverMessage.response_id;
          setIsResponding(false); // Response completed
          setMessages((prevMessages) => {
            // Check if there's a web search message with this response_id
            const webSearchMessage = prevMessages.find(
              (m) =>
                m.id === responseId &&
                (m.webSearchStatus === "started" ||
                  m.webSearchStatus === "completed")
            );

            if (webSearchMessage) {
              // If there's a web search message, check for a separate response message
              const newResponseId = `${responseId}_response`;
              const responseMessage = prevMessages.find(
                (m) => m.id === newResponseId
              );

              if (responseMessage) {
                // Update the response message (not the web search message)
                currentBotMessageId.current = null;
                return prevMessages.map((msg) =>
                  msg.id === newResponseId
                    ? {
                        ...msg,
                        isTypingPlaceholder: false,
                        content: serverMessage.final_content || msg.content,
                      }
                    : msg
                );
              } else {
                // No response message yet, just clear typing on web search message if needed
                currentBotMessageId.current = null;
                return prevMessages;
              }
            } else {
              // Normal completion - no web search message
              currentBotMessageId.current = null;
              return prevMessages.map((msg) =>
                msg.id === responseId
                  ? {
                      ...msg,
                      isTypingPlaceholder: false,
                      content: serverMessage.final_content || msg.content,
                    }
                  : msg
              );
            }
          });
          // Optimistically increment message_count for active history on assistant completion
          if (currentHistoryIdRef.current) {
            const nowIso = new Date().toISOString();
            setHistoryCards((prev) => {
              const cards = Array.isArray(prev) ? [...prev] : [];
              const idx = cards.findIndex(
                (c) => c.id === currentHistoryIdRef.current
              );
              if (idx === -1) return prev;
              const card = cards[idx];
              const updatedCard = {
                ...card,
                updated_at: nowIso,
                fromLocal: true,
                message_count: (card.message_count || 0) + 1,
              };
              cards.splice(idx, 1);
              cards.unshift(updatedCard);
              return cards;
            });
          }
          scrollToBottom();
          break;

        case "error":
          setChatError(
            serverMessage.message || "An error occurred with the chat server."
          );
          setIsConnected(ws.readyState === WebSocket.OPEN);
          setIsResponding(false); // Stop responding on error
          break;

        case "response_stopped":
          setIsResponding(false); // Stop responding
          // Remove loading dots from any message with this response_id
          const stoppedResponseId = serverMessage.response_id;
          setMessages((prevMessages) => {
            return prevMessages.map((msg) => {
              // Check if this message matches the response_id or is a response message
              if (
                msg.id === stoppedResponseId ||
                msg.id === `${stoppedResponseId}_response` ||
                (currentBotMessageId.current &&
                  msg.id === currentBotMessageId.current)
              ) {
                // Remove typing placeholder if it exists
                if (msg.isTypingPlaceholder) {
                  return {
                    ...msg,
                    isTypingPlaceholder: false,
                  };
                }
              }
              return msg;
            });
          });
          currentBotMessageId.current = null;
          break;

        case "ping":
          break;

        default:
          console.warn(
            "Chat.js: Unhandled WebSocket message type:",
            serverMessage.type
          );
      }
    };

    ws.onerror = (errorEvent) => {
      setIsConnecting(false);
      setIsConnected(false);
      setChatError("WebSocket connection error. Please try refreshing.");
    };

    ws.onclose = (event) => {
      setIsConnecting(false);
      setIsConnected(false);
      currentBotMessageId.current = null;
      if (!event.wasClean && event.code !== 1000) {
        setChatError("Disconnected from chat. Please refresh to reconnect.");
      }
    };

    return () => {
      if (wsInstance.current) {
        wsInstance.current.onclose = null;
        wsInstance.current.close(
          1000,
          "Component unmounting or personaId changing"
        );
        wsInstance.current = null;
      }
      setIsConnected(false);
      setIsConnecting(false);
    };
  }, [personaId, chatbot, loadingChatbot, scrollToBottom, historyIdParam]);

  useEffect(() => {
    if (!personaId) {
      setLoadingChatbot(false);
      setFetchError("No persona selected.");
      setChatbot(null);
      return;
    }
    setLoadingChatbot(true);
    setFetchError(null);

    const fetchPersonaDetails = async () => {
      try {
        const response = await fetch(
          `${config.api.baseUrl}/synthetic-audience/personas/${personaId}`,
          { credentials: "include" }
        );
        const data = await response.json();
        if (data.persona) {
          console.log("Persona data fetched:", data.persona);

          // Transform persona data to match chatbot interface
          const personaAsChatbot = {
            id: data.persona._id,
            name: data.persona.name,
            description: data.persona.description,
            prompt: data.persona.prompt,
            vector_store_id: data.persona.vector_store_id,
            files: data.persona.files || [],
            mcp_server_urls: []    // Personas don't use MCP servers
          };

          setChatbot(personaAsChatbot);
        } else {
          throw new Error(data.error || "Failed to fetch persona details");
        }
      } catch (err) {
        setFetchError(err.message || "Failed to load persona details.");
        setChatbot(null);
      } finally {
        setLoadingChatbot(false);
      }
    };
    fetchPersonaDetails();
  }, [personaId]);

  // Load chat history cards for this persona
  useEffect(() => {
    const migrateAndFetchHistoryCards = async () => {
      if (!personaId) {
        setHistoryCards([]);
        setHasMoreHistory(false);
        return;
      }
      setLoadingHistoryCards(true);
      setHistoryError(null);

      try {
        // First, attempt to migrate old persona chats (this is idempotent)
        await fetch(`${config.api.baseUrl}/synthetic-audience/personas/migrate-chats`, {
          method: 'POST',
          credentials: 'include'
        }).catch(() => {
          // Ignore migration errors - might not have any old chats
        });

        // Then fetch history cards using same endpoint as chatbots
        const url = `${config.api.baseUrl}${
          config.api.endpoints.chatbot.base
        }/history/cards?chatbot_id=${encodeURIComponent(personaId)}&limit=50&skip=0`;
        const res = await fetch(url, { credentials: "include" });
        const data = await res.json();

        if (data.success && Array.isArray(data.cards)) {
          // Transform cards to use 'id' instead of '_id' for consistency
          const transformedCards = data.cards.map(card => ({
            ...card,
            id: card._id
          }));
          setHistoryCards(transformedCards);
          setHasMoreHistory(data.has_more || false);
        } else {
          throw new Error(data.error || "Failed to load history.");
        }
      } catch (e) {
        setHistoryError(e.message || "Failed to load history.");
        setHistoryCards([]);
        setHasMoreHistory(false);
      } finally {
        setLoadingHistoryCards(false);
      }
    };
    migrateAndFetchHistoryCards();
  }, [personaId]);

  // Function to load more history cards (for infinite scroll)
  // NOTE: Personas don't support pagination yet, so this is disabled
  const loadMoreHistoryCards = useCallback(async () => {
    // Personas don't support pagination - hasMoreHistory will always be false
    return;
  }, []);

  // Infinite scroll for history cards
  useEffect(() => {
    const historyList = historyListRef.current;
    if (!historyList || historyCards.length === 0) {
      return;
    }

    // Wait a tick to ensure DOM has been updated with cards
    const setupTimeoutId = setTimeout(() => {
      let scrollTimeout = null;
      let lastCheckTime = 0;

      const checkAndLoadMore = () => {
        if (!historyList) return;
        
        // Throttle checks to max once per 200ms
        const now = Date.now();
        if (now - lastCheckTime < 200) return;
        lastCheckTime = now;
        
        const { scrollTop, scrollHeight, clientHeight } = historyList;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        
        // Load more when user scrolls to within 100px of bottom
        // Use ref for immediate check to prevent multiple simultaneous requests
        if (distanceFromBottom < 100 && hasMoreHistory && !loadingMoreHistoryRef.current) {
          loadMoreHistoryCards();
        }
      };

      const handleScroll = () => {
        // Clear any pending timeout
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        
        // Check after scrolling stops (debounced to 200ms)
        scrollTimeout = setTimeout(() => {
          checkAndLoadMore();
        }, 200);
      };

      historyList.addEventListener("scroll", handleScroll, { passive: true });
      
      // Store references for cleanup
      historyList._scrollHandler = handleScroll;
      historyList._scrollTimeout = scrollTimeout;
    }, 100);
    
    return () => {
      clearTimeout(setupTimeoutId);
      if (historyList) {
        if (historyList._scrollHandler) {
          historyList.removeEventListener("scroll", historyList._scrollHandler);
          delete historyList._scrollHandler;
        }
        if (historyList._scrollTimeout) {
          clearTimeout(historyList._scrollTimeout);
          delete historyList._scrollTimeout;
        }
      }
    };
  }, [hasMoreHistory, loadingMoreHistory, loadMoreHistoryCards, historyCards.length]);

  // Auto-open history if historyId query parameter is present
  useEffect(() => {
    const historyIdParam = searchParams.get("historyId");
    
    if (historyIdParam && historyCards.length > 0) {
      // Check if the history exists in the loaded cards
      const historyExists = historyCards.some(card => card.id === historyIdParam);
      
      // Only load if we haven't loaded this specific history yet
      // Check both currentHistoryId AND messages.length because currentHistoryId might be set
      // by websocket before messages are actually loaded
      const needsLoading = historyExists && (currentHistoryId !== historyIdParam || messages.length === 0);
      
      if (needsLoading) {
        // Load the history (loading state already set if coming from search)
        handleSelectHistoryCard(historyIdParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, historyCards, currentHistoryId, messages.length]);

  const handleToggleHistory = () => setIsHistoryOpen((prev) => !prev);

  // Listen for toggle history event from mobile header icon
  useEffect(() => {
    const handleToggleEvent = () => {
      handleToggleHistory();
    };
    
    window.addEventListener('toggleChatHistory', handleToggleEvent);
    
    return () => {
      window.removeEventListener('toggleChatHistory', handleToggleEvent);
    };
  }, []);

  // Dispatch history state changes to Layout for icon active state
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('chatHistoryStateChange', {
      detail: { isOpen: isHistoryOpen }
    }));
    
    // Close chatbots sidebar when history opens on mobile
    if (isHistoryOpen && window.innerWidth <= 630) {
      window.dispatchEvent(new CustomEvent('closeChatbotsSidebar'));
    }
  }, [isHistoryOpen]);

  // Listen for chatbots sidebar closing history sidebar
  useEffect(() => {
    const handleCloseChatHistory = () => {
      if (isHistoryOpen) {
        setIsHistoryOpen(false);
      }
    };
    
    window.addEventListener('closeChatHistory', handleCloseChatHistory);
    
    return () => {
      window.removeEventListener('closeChatHistory', handleCloseChatHistory);
    };
  }, [isHistoryOpen]);
  // Scroll to target message after loading from search
  useEffect(() => {
    if (!loadingFromSearch && targetMessageTimestamp && messages.length > 0) {
      // Find the message with matching timestamp
      const targetMessage = messages.find(msg => {
        if (!msg.timestamp || !targetMessageTimestamp) return false;
        // Compare ISO timestamps
        const msgTime = new Date(msg.timestamp).toISOString();
        const targetTime = new Date(targetMessageTimestamp).toISOString();
        return msgTime === targetTime;
      });

      if (targetMessage && messageRefs.current[targetMessage.id]) {
        // Scroll to the message with smooth behavior after a longer delay
        // to ensure the page has fully rendered
        setTimeout(() => {
          const element = messageRefs.current[targetMessage.id];
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center'
            });
            
            // Add a highlight effect
            element.classList.add('message-highlight');
            setTimeout(() => {
              element.classList.remove('message-highlight');
            }, 2000);
          }
        }, 500);
      }

      // Clear the target timestamp after scrolling
      setTargetMessageTimestamp(null);
    }
  }, [loadingFromSearch, targetMessageTimestamp, messages]);

  // Close context menu on outside click or escape
  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Don't close if clicking inside the share menu
      if (e.target.closest(".user-selector")) {
        return;
      }
      // Don't close if clicking the menu button or context menu itself
      if (e.target.closest(".history-card-menu-btn") || e.target.closest(".history-context-menu")) {
        return;
      }
      setContextMenu((s) => ({ ...s, visible: false }));
      setShareMenu((s) => ({ ...s, visible: false }));
      setSelectedUsers([]);
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setContextMenu((s) => ({ ...s, visible: false }));
        setShareMenu((s) => ({ ...s, visible: false }));
        setSelectedUsers([]);
      }
    };
    window.addEventListener("click", handleGlobalClick);
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("click", handleGlobalClick);
      window.removeEventListener("keydown", handleEsc);
    };
  }, []);

  const handleCardContextMenu = (e, cardId) => {
    e.preventDefault();
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, cardId });
  };

  const handleMenuButtonClick = (e, cardId) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get button position
    const buttonRect = e.currentTarget.getBoundingClientRect();
    
    const menuWidth = 180;
    const menuHeight = 200; // Approximate height
    
    let menuX, menuY;
    
    // On desktop (>1150px), use viewport-based positioning
    // On tablet/mobile (≤1150px), use sidebar-relative positioning
    if (window.innerWidth > 1150) {
      // Desktop: position relative to viewport
      menuX = buttonRect.left;
      menuY = buttonRect.bottom + 4;
      
      // Adjust if menu would go off right edge of viewport
      if (menuX + menuWidth > window.innerWidth - 8) {
        menuX = buttonRect.right - menuWidth;
      }
      
      // Adjust if menu would go off bottom edge of viewport
      if (menuY + menuHeight > window.innerHeight - 8) {
        menuY = buttonRect.top - menuHeight - 4;
      }
      
      // Ensure menu doesn't go off left edge
      if (menuX < 8) {
        menuX = 8;
      }
    } else {
      // Tablet/Mobile: position relative to sidebar panel
      const sidebarPanel = document.querySelector('.history-sidebar-panel');
      const sidebarRect = sidebarPanel ? sidebarPanel.getBoundingClientRect() : null;
      
      // Calculate position relative to sidebar panel
      menuX = buttonRect.left - (sidebarRect ? sidebarRect.left : 0);
      menuY = buttonRect.bottom - (sidebarRect ? sidebarRect.top : 0) + 4;
      
      // Get sidebar width for boundary checks
      const sidebarWidth = sidebarRect ? sidebarRect.width : window.innerWidth;
      const sidebarHeight = sidebarRect ? sidebarRect.height : window.innerHeight;
      
      // Adjust if menu would go off right edge of sidebar
      if (menuX + menuWidth > sidebarWidth - 8) {
        menuX = Math.max(8, sidebarWidth - menuWidth - 8);
      }
      
      // Adjust if menu would go off bottom edge of sidebar
      if (menuY + menuHeight > sidebarHeight - 8) {
        menuY = buttonRect.top - (sidebarRect ? sidebarRect.top : 0) - menuHeight - 4;
      }
      
      // Ensure menu doesn't go off left edge
      if (menuX < 8) {
        menuX = 8;
      }
    }
    
    setContextMenu({ 
      visible: true, 
      x: menuX,
      y: menuY, 
      cardId 
    });
  };

  const handleDeleteConversation = async (cardId) => {
    try {
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${cardId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const data = await res.json();
      if (!data.success && !res.ok) throw new Error(data.error || "Failed to delete");
      setHistoryCards((prev) => prev.filter((c) => c.id !== cardId));
      if (currentHistoryIdRef.current === cardId) {
        setCurrentHistoryId(null);
        currentHistoryIdRef.current = null;
        setMessages([]);
        setHasMoreMessages(false);
        setTotalMessageCount(0);
        // Inform server to reset context so next message starts a new conversation
        if (
          wsInstance.current &&
          wsInstance.current.readyState === WebSocket.OPEN
        ) {
          try {
            wsInstance.current.send(
              JSON.stringify({ type: "init", persona_id: personaId })
            );
          } catch (e) {
            // ignore
          }
        }
      }
    } catch (err) {
      setHistoryError(err.message || "Failed to delete conversation.");
    } finally {
      setContextMenu((s) => ({ ...s, visible: false }));
    }
  };

  const handleDuplicateConversation = async (cardId) => {
    try {
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${cardId}/duplicate`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to duplicate");
      const card = data.card;
      // Add to top of history list optimistically
      setHistoryCards((prev) => [{ ...card }, ...(prev || [])]);
      setContextMenu((s) => ({ ...s, visible: false }));
    } catch (err) {
      setHistoryError(err.message || "Failed to duplicate conversation.");
      setContextMenu((s) => ({ ...s, visible: false }));
    }
  };

  const handleShareClick = (e, cardId) => {
    e.preventDefault();
    e.stopPropagation();
    // Position share menu to touch the right edge of context menu
    // Use setTimeout to ensure the context menu is rendered and we can measure it
    setTimeout(() => {
      const contextMenuElement = document.querySelector(
        ".history-context-menu"
      );
      const contextMenuWidth = contextMenuElement
        ? contextMenuElement.offsetWidth
        : 180;
      setShareMenu({
        visible: true,
        x: contextMenu.x + contextMenuWidth, // Position at the right edge
        y: contextMenu.y,
        cardId: cardId,
      });
    }, 0);
    setSelectedUsers([]);
  };

  const handleShareConversation = async (cardId, users) => {
    if (!users || users.length === 0) {
      setHistoryError("Please select at least one user to share with.");
      return;
    }

    try {
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${cardId}/share`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ user_emails: users.map((u) => u.email) }),
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to share");

      // Close menus and reset state
      setShareMenu((s) => ({ ...s, visible: false }));
      setContextMenu((s) => ({ ...s, visible: false }));
      setSelectedUsers([]);

      // Optionally show success message or refresh history
      // The shared conversations will appear in the recipients' history
    } catch (err) {
      setHistoryError(err.message || "Failed to share conversation.");
      setShareMenu((s) => ({ ...s, visible: false }));
    }
  };

  const handleEditTitle = (cardId, currentTitle) => {
    setEditingTitleId(cardId);
    setEditingTitleValue(currentTitle || "Untitled");
    setContextMenu((s) => ({ ...s, visible: false }));
  };

  const handleSaveTitle = async (cardId) => {
    const newTitle = editingTitleValue.trim() || "Untitled";

    try {
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${cardId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ title: newTitle }),
        }
      );
      const data = await res.json();
      if (!data.success)
        throw new Error(data.error || "Failed to update title");

      // Update the card optimistically
      setHistoryCards((prev) =>
        prev.map((c) =>
          c.id === cardId ? { ...c, title: data.title || newTitle } : c
        )
      );

      setEditingTitleId(null);
      setEditingTitleValue("");
    } catch (err) {
      setHistoryError(err.message || "Failed to update title.");
      setEditingTitleId(null);
      setEditingTitleValue("");
    }
  };

  const handleCancelEditTitle = () => {
    setEditingTitleId(null);
    setEditingTitleValue("");
  };

  const handleSelectHistoryCard = async (cardId) => {
    try {
      // Fetch chat history (same endpoint as chatbots)
      const res = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.chatbot.base}/history/${cardId}?limit=20&skip=0`,
        { credentials: "include" }
      );
      const data = await res.json();
      if (data.success && data.history) {
        // Debug: Log RAW data from API before transformation
        console.log("=".repeat(80));
        console.log("🔍 RAW CHAT DATA FROM API");
        console.log("=".repeat(80));
        console.log("Chat ID:", cardId);
        console.log(
          "Messages loaded:",
          data.history.messages?.length || 0
        );
        console.log(
          "Total messages:",
          data.history.total_messages || 0
        );
        console.log(
          "Has more:",
          data.history.has_more
        );
        console.log("Raw messages data:", data.history.messages);
        console.log("Full history object:", data.history);
        console.log("=".repeat(80));

        // Store pagination metadata
        setTotalMessageCount(data.history.total_messages || 0);
        setHasMoreMessages(data.history.has_more || false);

        const msgs = (data.history.messages || [])
          .map((m, idx) => {
            // Persona messages have a simpler structure: role, content, timestamp
            // Filter out events that personas don't support
            if (m.event === "function_call_result") {
              return null;
            }

            // Ensure content is always a string
            let content = m.content || "";
            if (typeof content !== 'string') {
              console.warn(`Message ${idx} has non-string content:`, typeof content, content);
              // Check if it's the response object from stream_openai_response
              if (content && content.full_assistant_response) {
                content = content.full_assistant_response;
              } else {
                content = JSON.stringify(content);
              }
            }

            const msg = {
              id: `${cardId}-${idx}`,
              role: m.role,
              content: content,
              timestamp: m.timestamp || new Date().toISOString(),
              fromHistory: true,
            };
            if (Array.isArray(m.attachments) && m.attachments.length > 0) {
              msg.attachments = m.attachments;
            }
            return msg;
          })
          .filter(Boolean); // Remove null entries
        setMessages(msgs);

        // Debug: Log conversation details when loading from history
        console.log("=".repeat(80));
        console.log("🔍 CONVERSATION LOADED FROM HISTORY");
        console.log("=".repeat(80));
        console.log("\n📋 MESSAGES:");
        console.log("Total messages:", msgs.length);
        console.log("Messages:", msgs);

        console.log("\n🔧 STATE:");
        console.log("Chat History ID:", cardId);
        console.log("Persona ID:", personaId);
        console.log("=".repeat(80));

        setCurrentHistoryId(cardId);
        currentHistoryIdRef.current = cardId;

        // Clear URL parameters to prevent auto-loading conflicts when manually selecting history
        navigate(`/synthetic-audience-workshop/personas/${personaId}/chat`, { replace: true });

        // if ws is open, re-init with this history id to ensure server writes to same doc
        if (
          wsInstance.current &&
          wsInstance.current.readyState === WebSocket.OPEN
        ) {
          try {
            wsInstance.current.send(
              JSON.stringify({
                type: "init",
                persona_id: personaId,
                chat_id: cardId,
              })
            );
          } catch (e) {
            // ignore
          }
        }

        // Close sidebar on mobile (630px and below)
        if (window.innerWidth <= 630) {
          setIsHistoryOpen(false);
        }
        
        // Clear loading state after a delay to ensure messages are fully rendered
        setTimeout(() => {
          setLoadingFromSearch(false);
        }, 500);
      } else {
        throw new Error(data.error || "Failed to load chat history.");
      }
    } catch (e) {
      setChatError(e.message || "Failed to load chat history.");
      setLoadingFromSearch(false);
    }
  };

  // Function to load older messages when scrolling to top
  // NOTE: Personas don't support message pagination yet - all messages load at once
  const loadOlderMessages = useCallback(async () => {
    // Disabled for personas - hasMoreMessages will always be false
    return;
  }, []);

  // Scroll listener for loading older messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop } = container;
      
      // Load more when user scrolls within 100px of the top
      if (scrollTop < 100 && hasMoreMessages && !loadingOlderMessagesRef.current) {
        loadOlderMessages();
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [hasMoreMessages, loadOlderMessages, messages.length]);


  // Format a UTC ISO string to local MST time (subtract 7 hours) and show only h:mm AM/PM
  const formatMSTTimeFromUTC = (isoString) => {
    try {
      const parsed = new Date(isoString);
      if (isNaN(parsed.getTime())) return "";
      const adjusted = new Date(parsed.getTime() - 7 * 60 * 60 * 1000);
      return adjusted.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return "";
    }
  };

  // Choose correct display format based on message origin
  const formatMessageTime = (message) => {
    const ts = message?.timestamp;
    if (!ts) return "";
    if (message?.fromHistory) {
      return formatMSTTimeFromUTC(ts);
    }
    return new Date(ts).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const submitMessage = async (content, filesToSubmit) => {
    // Allow sending if there's text OR attachments
    if (
      (!content.trim() && filesToSubmit.length === 0) ||
      !wsInstance.current ||
      wsInstance.current.readyState !== WebSocket.OPEN
    ) {
      if (!isConnected) setChatError("Cannot send: Not connected.");
      return;
    }

    const userMessageContent = content.trim();

    const userMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: userMessageContent,
      timestamp: new Date().toISOString(),
      attachments: filesToSubmit, // Store the actual files with the message object for UI rendering
    };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setChatError(null);

    let attachmentsPayload = [];

    // If there are files, read and encode them
    if (filesToSubmit.length > 0) {
      const readFilesPromises = filesToSubmit.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file); // Reads as Base64 Data URL
          reader.onload = () => {
            resolve({
              name: file.name,
              type: file.type,
              content: reader.result, // The Base64 string
            });
          };
          reader.onerror = (error) => reject(error);
        });
      });

      try {
        // Wait for all files to be read before continuing
        attachmentsPayload = await Promise.all(readFilesPromises);
      } catch (error) {
        console.error("Error reading attached files:", error);
        setChatError("Failed to read attachments. Message not sent.");
        // Optional: remove the optimistic user message from UI if file reading fails
        setMessages((prev) => prev.filter((msg) => msg.id !== userMessage.id));
        return;
      }
    }

    // Now clear the attachments from the UI since they are processed
    setAttachedFiles([]);
    // Revoke all preview URLs and clear the preview state
    attachmentPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    setAttachmentPreviews([]);

    const payload = {
      type: "user_message",
      content: userMessageContent,
      attachments: attachmentsPayload,
      web_search_enabled: webSearchEnabled, // Include web search toggle state
    };
    if (currentHistoryIdRef.current) {
      payload.chat_history_id = currentHistoryIdRef.current;
    }
    wsInstance.current.send(JSON.stringify(payload));

    // Optimistically update history card timestamp and move it to top
    if (currentHistoryIdRef.current) {
      // Use current UTC timestamp; formatting logic handles display
      const nowIso = new Date().toISOString();
      setHistoryCards((prev) => {
        const cards = Array.isArray(prev) ? [...prev] : [];
        const idx = cards.findIndex(
          (c) => c.id === currentHistoryIdRef.current
        );
        if (idx === -1) return prev; // Card not found; it will be added by history_opened if needed
        const card = cards[idx];
        const updatedCard = {
          ...card,
          updated_at: nowIso,
          fromLocal: true,
          message_count: (card.message_count || 0) + 1,
        };
        cards.splice(idx, 1);
        cards.unshift(updatedCard);
        return cards;
      });
    }
  };

  const handleSendFromEditor = () => {
    const md = getEditorMarkdown();
    if (!md.trim() && attachedFiles.length === 0) return;
    submitMessage(md, attachedFiles);
    clearEditor();
  };

  const handleSendMessage = () => {
    // Fallback if ever used; route to editor send
    handleSendFromEditor();
  };

  const handleEditMessage = (messageId, currentContent) => {
    setEditingMessageId(messageId);
    setEditedContent(currentContent);
  };

  const handleSaveEdit = async (messageId) => {
    if (!editedContent.trim()) {
      // Don't save empty edits
      setEditingMessageId(null);
      setEditedContent("");
      return;
    }

    // Find the index of the message being edited
    const messageIndex = messages.findIndex((msg) => msg.id === messageId);
    if (messageIndex === -1) {
      setEditingMessageId(null);
      setEditedContent("");
      return;
    }

    const originalMessage = messages[messageIndex];
    const originalContent = originalMessage.content;

    // Remove all messages after the edited message
    const updatedMessages = messages.slice(0, messageIndex + 1);

    // Update the edited message
    updatedMessages[messageIndex] = {
      ...updatedMessages[messageIndex],
      content: editedContent.trim(),
      timestamp: new Date().toISOString(),
    };

    setMessages(updatedMessages);
    setEditingMessageId(null);
    setEditedContent("");

    // Send the edited message as a new message to continue the conversation
    if (
      wsInstance.current &&
      wsInstance.current.readyState === WebSocket.OPEN
    ) {
      const payload = {
        type: "user_message",
        content: editedContent.trim(),
        attachments: [], // Edit doesn't support attachments for now
        web_search_enabled: webSearchEnabled,
        is_edit: true, // Flag to indicate this is an edit
        edit_original_content: originalContent, // Original content to find the message in history
      };
      if (currentHistoryIdRef.current) {
        payload.chat_history_id = currentHistoryIdRef.current;
      }
      wsInstance.current.send(JSON.stringify(payload));

      // Update history card
      if (currentHistoryIdRef.current) {
        const nowIso = new Date().toISOString();
        setHistoryCards((prev) => {
          const cards = Array.isArray(prev) ? [...prev] : [];
          const idx = cards.findIndex(
            (c) => c.id === currentHistoryIdRef.current
          );
          if (idx === -1) return prev;
          const card = cards[idx];
          const updatedCard = {
            ...card,
            updated_at: nowIso,
            fromLocal: true,
            message_count: (card.message_count || 0) + 1,
          };
          cards.splice(idx, 1);
          cards.unshift(updatedCard);
          return cards;
        });
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditedContent("");
  };

  const handleCopyMessage = async (content, messageId) => {
    try {
      await navigator.clipboard.writeText(content);
      // Show confirmation popup
      setCopiedMessageId(messageId);
      // Auto-hide after 2 seconds
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy message:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = content;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        // Show confirmation popup even for fallback
        setCopiedMessageId(messageId);
        setTimeout(() => {
          setCopiedMessageId(null);
        }, 2000);
      } catch (fallbackErr) {
        console.error("Fallback copy failed:", fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleStopResponse = () => {
    console.log("*** FRONTEND: handleStopResponse called ***");
    console.log("WebSocket state:", wsInstance.current?.readyState);
    console.log("Current bot message ID:", currentBotMessageId.current);

    if (
      !wsInstance.current ||
      wsInstance.current.readyState !== WebSocket.OPEN
    ) {
      console.error("*** FRONTEND: Cannot send stop - WebSocket not ready ***");
      return;
    }

    // Send stop message to backend
    try {
      const stopMessage = {
        type: "stop_response",
        response_id: currentBotMessageId.current,
      };
      console.log("*** FRONTEND: Sending stop message ***", stopMessage);
      wsInstance.current.send(JSON.stringify(stopMessage));
      console.log("*** FRONTEND: Stop message sent successfully ***");

      // Immediately update UI state
      setIsResponding(false);

      // Remove loading dots from the current message if it exists
      if (currentBotMessageId.current) {
        setMessages((prevMessages) => {
          return prevMessages.map((msg) => {
            // Check if this message matches the current bot message ID
            if (
              msg.id === currentBotMessageId.current ||
              msg.id === `${currentBotMessageId.current}_response`
            ) {
              // Remove typing placeholder if it exists
              if (msg.isTypingPlaceholder) {
                return {
                  ...msg,
                  isTypingPlaceholder: false,
                };
              }
            }
            return msg;
          });
        });
      }

      currentBotMessageId.current = null;
    } catch (error) {
      console.error("*** FRONTEND: Error sending stop message ***", error);
    }
  };

  const handleStarterQuestionClick = (question) => {
    submitMessage(question, []);
  };


  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      // Add the raw File objects to the state for submission
      setAttachedFiles((prevFiles) => [...prevFiles, ...newFiles]);

      // Create new preview objects with URLs for rendering
      const newPreviews = newFiles.map((file) => ({
        file: file,
        url: URL.createObjectURL(file),
      }));
      setAttachmentPreviews((prevPreviews) => [
        ...prevPreviews,
        ...newPreviews,
      ]);
    }
    // Reset the file input's value to allow selecting the same file again.
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // New handler to remove a specific file
  const handleRemoveFile = (fileIndex) => {
    // Get the preview to revoke its URL before removing from state
    const previewToRemove = attachmentPreviews[fileIndex];
    if (previewToRemove) {
      URL.revokeObjectURL(previewToRemove.url);
    }

    // Remove the file and its preview from their respective states
    setAttachedFiles((prevFiles) =>
      prevFiles.filter((_, index) => index !== fileIndex)
    );
    setAttachmentPreviews((prevPreviews) =>
      prevPreviews.filter((_, index) => index !== fileIndex)
    );
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if dragging files (not text or other elements)
    if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      // Don't show overlay if hovering over history sidebar or its children
      const historyElements = e.target.closest('.history-sidebar-wrapper, .history-sidebar-panel');
      if (!historyElements) {
        setIsDraggingFile(true);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if we're over the history sidebar
    const historyElements = e.target.closest('.history-sidebar-wrapper, .history-sidebar-panel');
    if (historyElements) {
      // Hide overlay if we move over the history sidebar
      setIsDraggingFile(false);
    } else if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
      // Show overlay if we're dragging files over the main chat area
      setIsDraggingFile(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only hide overlay if leaving the main chat area (not just moving between child elements)
    const relatedTarget = e.relatedTarget;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDraggingFile(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);

    // Don't process drops on the history sidebar
    const historyElements = e.target.closest('.history-sidebar-wrapper, .history-sidebar-panel');
    if (historyElements) {
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      // Add the raw File objects to the state for submission
      setAttachedFiles((prevFiles) => [...prevFiles, ...newFiles]);

      // Create new preview objects with URLs for rendering
      const newPreviews = newFiles.map((file) => ({
        file: file,
        url: URL.createObjectURL(file),
      }));
      setAttachmentPreviews((prevPreviews) => [
        ...prevPreviews,
        ...newPreviews,
      ]);
    }
  };

  if (loadingChatbot) {
    return (
      <Layout>
        <div className="chat-page">
          {loadingFromSearch && (
            <div className="chat-loading-overlay">
              <div className="chat-loading-content">
                <div className="chat-loading-spinner"></div>
                <p className="chat-loading-text">Loading conversation...</p>
              </div>
            </div>
          )}
          <div className="chat-container" style={{ display: loadingFromSearch ? 'none' : 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <div className="chat-loading-content">
              <div className="chat-loading-spinner"></div>
              <p className="chat-loading-text">Loading persona...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }
  if (fetchError) {
    return (
      <Layout>
        <div className="chat-container">
          <div className="error-message">{fetchError}</div>
        </div>
      </Layout>
    );
  }
  if (!chatbot) {
    return (
      <Layout>
        <div className="chat-container">
          <div className="error-message">Chatbot data not available.</div>
        </div>
      </Layout>
    );
  }

  const hasMessages = messages.length > 0;
  const chatbotVisual = getChatbotVisual();
  const userInitials = getUserInitials();
  const hasStarterQuestions =
    chatbot &&
    Array.isArray(chatbot.starter_questions) &&
    chatbot.starter_questions.length > 0;

  return (
    <Layout>
      <div 
        className="chat-page"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {loadingFromSearch && (
          <div className="chat-loading-overlay">
            <div className="chat-loading-content">
              <div className="chat-loading-spinner"></div>
              <p className="chat-loading-text">Loading conversation...</p>
            </div>
          </div>
        )}
        <div className="chat-container" style={{ display: loadingFromSearch ? 'none' : 'block' }}>
          <div
            className={`chat-history-page-container ${
              isHistoryOpen ? "history-open" : "history-closed"
            }`}
            onClick={(e) => {
              // Close history sidebar when clicking outside of it (on backdrop/chat panel)
              // Only at 1150px and below when the sidebar overlays content
              if (
                isHistoryOpen &&
                window.innerWidth <= 1150 &&
                !e.target.closest(".history-sidebar-wrapper")
              ) {
                setIsHistoryOpen(false);
              }
            }}
          >
            <div 
              className="history-sidebar-wrapper"
              onClick={(e) => {
                // Open sidebar when clicking anywhere on it while closed
                if (!isHistoryOpen && !e.target.closest('button')) {
                  handleToggleHistory();
                }
              }}
              style={{ cursor: !isHistoryOpen ? 'pointer' : 'default' }}
            >
              <div className="history-sidebar-panel">
                <div className="history-header">
                  <button
                    className="new-chat-btn"
                    title="Start new conversation"
                    onClick={() => {
                      // Start a fresh conversation without refreshing the page
                      setMessages([]);
                      setHasMoreMessages(false);
                      setTotalMessageCount(0);
                      setCurrentHistoryId(null);
                      currentHistoryIdRef.current = null;
                      setChatError(null);
                      setInputText("");
                      // Clear attachments and previews
                      setAttachedFiles([]);
                      attachmentPreviews.forEach((p) =>
                        URL.revokeObjectURL(p.url)
                      );
                      setAttachmentPreviews([]);
                      // Clear URL parameters to prevent auto-loading from search results
                      navigate(`/synthetic-audience-workshop/personas/${personaId}/chat`, { replace: true });
                      // Inform server to reset conversation context for this persona
                      if (
                        wsInstance.current &&
                        wsInstance.current.readyState === WebSocket.OPEN
                      ) {
                        try {
                          wsInstance.current.send(
                            JSON.stringify({
                              type: "init",
                              persona_id: personaId,
                            })
                          );
                        } catch (e) {
                          // ignore
                        }
                      }
                      // Close sidebar on mobile (630px and below)
                      if (window.innerWidth <= 630) {
                        setIsHistoryOpen(false);
                      }
                    }}
                  >
                    <img src={plusIcon} alt="New" className="new-chat-icon" />
                  </button>
                  <button
                    onClick={handleToggleHistory}
                    className="history-sidebar-toggle-btn"
                    title={isHistoryOpen ? "Close History" : "Open History"}
                  >
                    <img
                      src={historyIconDark}
                      alt="History"
                      className="history-toggle-icon"
                    />
                  </button>
                </div>
                {loadingHistoryCards && (
                  <div className="history-loading">Loading…</div>
                )}
                {historyError && (
                  <div className="history-error">{historyError}</div>
                )}
                {!loadingHistoryCards &&
                  !historyError &&
                  historyCards.length === 0 && (
                    <div className="history-empty">
                      Conversations will be saved here
                    </div>
                  )}
                <div className="history-list" ref={historyListRef}>
                  {historyCards.map((card) => (
                    <div
                      key={card.id}
                      className={`history-card ${
                        currentHistoryId === card.id ? "active" : ""
                      } ${card.is_shared ? "shared" : ""}`}
                      onContextMenu={(e) => handleCardContextMenu(e, card.id)}
                      onClick={(e) => {
                        // Don't select card if clicking the menu button
                        if (e.target.closest('.history-card-menu-btn')) {
                          return;
                        }
                        if (editingTitleId !== card.id) {
                          handleSelectHistoryCard(card.id);
                          // Close sidebar on mobile (630px and below)
                          if (window.innerWidth <= 630) {
                            setIsHistoryOpen(false);
                          }
                        }
                      }}
                    >
                      <div className="history-card-content">
                        {editingTitleId === card.id ? (
                          <input
                            type="text"
                            className="history-card-title-input"
                            value={editingTitleValue}
                            onChange={(e) => setEditingTitleValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSaveTitle(card.id);
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                handleCancelEditTitle();
                              }
                            }}
                            onBlur={() => handleSaveTitle(card.id)}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <div className="history-card-title">
                            {card.is_imported && (
                              <img 
                                src={openaiLogo} 
                                alt="Imported from ChatGPT" 
                                className="history-card-import-icon"
                                title="Imported from ChatGPT"
                              />
                            )}
                            {card.title || "Untitled"}
                          </div>
                        )}
                        {card.is_shared && card.shared_by_email && (
                          <div className="history-card-shared-by">
                            Shared by: {card.shared_by_email}
                          </div>
                        )}
                        <div className="history-card-sub">
                          {card.message_count || 0} messages ·{" "}
                          {formatHistoryCardTime(card)}
                        </div>
                      </div>
                      <button
                        className="history-card-menu-btn"
                        onClick={(e) => handleMenuButtonClick(e, card.id)}
                        aria-label="Options"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle cx="8" cy="3" r="1.5" />
                          <circle cx="8" cy="8" r="1.5" />
                          <circle cx="8" cy="13" r="1.5" />
                        </svg>
                      </button>
                    </div>
                  ))}
                  {loadingMoreHistory && (
                    <div className="history-loading-more">
                      <div className="spinner"></div>
                      <span>Loading more...</span>
                    </div>
                  )}
                  {!loadingMoreHistory && historyCards.length > 0 && !hasMoreHistory && (
                    <div className="history-end-message">No more conversations</div>
                  )}
                </div>
                {contextMenu.visible && (
                  <div
                    className={`history-context-menu ${
                      shareMenu.visible ? "share-menu-open" : ""
                    }`}
                    style={{
                      position: "fixed",
                      left: contextMenu.x,
                      top: contextMenu.y,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="menu-item duplicate"
                      onClick={() =>
                        handleDuplicateConversation(contextMenu.cardId)
                      }
                    >
                      Duplicate Conversation
                    </div>
                    <div
                      className="menu-item share"
                      onClick={(e) => handleShareClick(e, contextMenu.cardId)}
                    >
                      Share
                    </div>
                    <div
                      className="menu-item"
                      onClick={() => {
                        const card = historyCards.find(
                          (c) => c.id === contextMenu.cardId
                        );
                        if (card) {
                          handleEditTitle(contextMenu.cardId, card.title);
                        }
                      }}
                    >
                      Edit Title
                    </div>
                    <div
                      className="menu-item danger"
                      onClick={() =>
                        handleDeleteConversation(contextMenu.cardId)
                      }
                    >
                      Delete Conversation
                    </div>
                  </div>
                )}
                {shareMenu.visible && (
                  <div
                    className="share-menu-container"
                    style={{
                      position: "fixed",
                      left: shareMenu.x,
                      top: shareMenu.y,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <UserSelector
                      selectedUsers={selectedUsers}
                      onChange={setSelectedUsers}
                      onShare={() =>
                        handleShareConversation(shareMenu.cardId, selectedUsers)
                      }
                      onClose={() => {
                        setShareMenu((s) => ({ ...s, visible: false }));
                        setSelectedUsers([]);
                      }}
                      shareDisabled={selectedUsers.length === 0}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="chat-panel">
              {isDraggingFile && (
                <div className="file-drop-overlay">
                  <div className="file-drop-content">
                    <svg 
                      width="56" 
                      height="56" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <p className="file-drop-text">Drop file here to attach</p>
                  </div>
                </div>
              )}
              <div className="chat-div">
                {isConnecting && (
                  <div className="info-message connecting">
                    Connecting to chat...
                  </div>
                )}
                {!isConnected && !isConnecting && !chatError && personaId && (
                  <div className="info-message disconnected">
                    Not connected. Attempting to reconnect...
                  </div>
                )}

                <div className="chat-messages" ref={messagesContainerRef}>
                  {!hasMessages ? (
                    <div className="empty-chat-fullheight">
                      {chatbotVisual ? (
                        <div className="welcome-container">
                          <div
                            className="chatbot-icon large-icon"
                            dangerouslySetInnerHTML={{
                              __html:
                                getChatbotVisual("logo") ||
                                getChatbotVisual("icon"),
                            }}
                          />
                          {/* <p className="empty-chat-description">
                        {chatbot.description}
                      </p> */}
                          {hasStarterQuestions && (
                            <div className="starter-questions-container">
                              {chatbot.starter_questions.map(
                                (question, index) => (
                                  <div
                                    key={index}
                                    className="starter-question"
                                    onClick={() =>
                                      handleStarterQuestionClick(question)
                                    }
                                  >
                                    {question}
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="welcome-container">
                          <h2 className="persona-name">{chatbot.name}</h2>
                          <p className="empty-chat-description">
                            {chatbot.description}
                          </p>
                          {hasStarterQuestions && (
                            <div className="starter-questions-container">
                              {chatbot.starter_questions.map(
                                (question, index) => (
                                  <div
                                    key={index}
                                    className="starter-question"
                                    onClick={() =>
                                      handleStarterQuestionClick(question)
                                    }
                                  >
                                    {question}
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {loadingOlderMessages && (
                        <div className="loading-older-messages">
                          <div className="spinner"></div>
                          <span>Loading older messages...</span>
                        </div>
                      )}
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          ref={(el) => {
                            if (el) {
                              messageRefs.current[message.id] = el;
                            }
                          }}
                          className={`chat-message ${
                            message.role === "user" ? "user" : "bot"
                          }`}
                        >
                          {message.role === "user" ? (
                            <div className="message-avatar user-avatar">
                              <span>{userInitials}</span>
                            </div>
                          ) : (
                            <div className="message-avatar bot-avatar">
                              {chatbotVisual ? (
                                <div
                                  className="bot-icon"
                                  dangerouslySetInnerHTML={{
                                    __html: chatbotVisual,
                                  }}
                                />
                              ) : (
                                <div className="bot-icon default-icon">
                                  <img
                                    src={ltLogo}
                                    alt="AI Avatar"
                                    className="bot-avatar-svg"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                          <div className="message-wrapper">
                            {/* Render attachments OUTSIDE/ABOVE the message content bubble */}
                            {message.role === "user" &&
                              message.attachments &&
                              message.attachments.length > 0 && (
                                <div className="message-attachments-preview">
                                  {message.attachments.map((file, index) => {
                                    // Handle both File objects (live messages) and attachment objects (from history)
                                    const isFileObject = file instanceof File;
                                    // For File objects, create object URL; for history attachments, load from server
                                    let fileSrc;
                                    if (isFileObject) {
                                      fileSrc = URL.createObjectURL(file);
                                    } else if (file.file_path) {
                                      fileSrc = `${config.api.baseUrl}/synthetic-audience/attachments/${encodeURIComponent(file.file_path)}`;
                                    } else if (file.content) {
                                      fileSrc = file.content; // Legacy: base64 data URL
                                    }

                                    const isImage =
                                      file.type?.startsWith("image/");
                                    const isPDF =
                                      file.type === "application/pdf" ||
                                      file.name?.toLowerCase().endsWith(".pdf");
                                    const isWordDoc =
                                      file.type === "application/msword" ||
                                      file.type ===
                                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".doc") ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".docx");
                                    const isCSV =
                                      file.type === "text/csv" ||
                                      file.name?.toLowerCase().endsWith(".csv");
                                    const isExcel =
                                      file.type ===
                                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                                      file.type ===
                                        "application/vnd.ms-excel" ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".xlsx") ||
                                      file.name?.toLowerCase().endsWith(".xls");
                                    const isTextFile =
                                      file.type?.startsWith("text/") ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".txt") ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".md") ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".markdown") ||
                                      file.name
                                        ?.toLowerCase()
                                        .endsWith(".json") ||
                                      file.name?.toLowerCase().endsWith(".js");
                                    const hasPreview =
                                      isPDF ||
                                      isWordDoc ||
                                      isCSV ||
                                      isExcel ||
                                      isTextFile;

                                    return (
                                      <div
                                        key={index}
                                        className="message-attachment-item"
                                      >
                                        {isImage && fileSrc ? (
                                          <img src={fileSrc} alt={file.name} />
                                        ) : file.preview_path ? (
                                          <img
                                            src={`${
                                              config.api.baseUrl
                                            }/chatbot/files/${encodeURIComponent(
                                              file.preview_path
                                            )}`}
                                            alt={file.name}
                                            className="attachment-pdf-preview-image"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        ) : isPDF && file.file_path ? (
                                          <img
                                            src={`${
                                              config.api.baseUrl
                                            }/chatbot/preview/pdf/${encodeURIComponent(
                                              file.file_path
                                            )}`}
                                            alt={file.name}
                                            className="attachment-pdf-preview-image"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        ) : isWordDoc && file.file_path ? (
                                          <img
                                            src={`${
                                              config.api.baseUrl
                                            }/chatbot/preview/word/${encodeURIComponent(
                                              file.file_path
                                            )}`}
                                            alt={file.name}
                                            className="attachment-word-preview-image"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        ) : hasPreview && file.file_path ? (
                                          <img
                                            src={`${
                                              config.api.baseUrl
                                            }/chatbot/preview/file/${encodeURIComponent(
                                              file.file_path
                                            )}`}
                                            alt={file.name}
                                            className="attachment-word-preview-image"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        ) : null}
                                        <span
                                          className="message-attachment-name"
                                          title={file.name}
                                        >
                                          {file.name}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            <div
                              className={`message-content ${
                                message.role === "assistant" &&
                                (message.functionCallStatus === "started" ||
                                  message.webSearchStatus === "started" ||
                                  (message.functionCallStatus === "completed" &&
                                    !message.content) ||
                                  (message.webSearchStatus === "completed" &&
                                    !message.content))
                                  ? "function-call-padding"
                                  : ""
                              }`}
                            >
                              {/* Text content of the message */}
                              {message.role === "assistant" ? (
                                <>
                                  {/* Prioritize web search status over typing indicator */}
                                  {/* Web Search Status Indicator */}
                                  {message.webSearchStatus === "started" && (
                                    <div className="web-search-status">
                                      <div className="web-search-spinner"></div>
                                      <span>
                                        {message.webSearchAction === "search"
                                          ? "Searching the web..."
                                          : "Browsing..."}
                                      </span>
                                    </div>
                                  )}

                                  {/* Code Interpreter Status Indicator */}
                                  {message.codeInterpreterStatus ===
                                    "started" &&
                                    !message.content && (
                                      <div className="web-search-status">
                                        <div className="web-search-spinner"></div>
                                        <span>Running code...</span>
                                      </div>
                                    )}

                                  {/* Function Call Status Indicator */}
                                  {message.functionCallStatus === "started" && (
                                    <div className="function-call-status">
                                      <div className="function-call-spinner"></div>
                                      <span>
                                        Calling function:{" "}
                                        {message.functionName ||
                                          "External Tool"}
                                        ...
                                      </span>
                                    </div>
                                  )}
                                  {message.webSearchStatus === "completed" &&
                                  !message.content ? (
                                    // Show "Web search complete" briefly, then loading dots will appear
                                    <div className="function-call-status">
                                      Web search complete
                                    </div>
                                  ) : message.codeInterpreterStatus ===
                                      "completed" && !message.content ? (
                                    <div className="function-call-status">
                                      Code interpreter complete
                                    </div>
                                  ) : message.imageGenerationStatus ===
                                    "started" ? (
                                    <div className="function-call-status">
                                      Generating image...
                                    </div>
                                  ) : message.functionCallStatus ===
                                      "completed" && !message.content ? (
                                    <div className="function-call-status">
                                      Function call complete
                                    </div>
                                  ) : message.isTypingPlaceholder &&
                                    !message.content &&
                                    !message.webSearchStatus &&
                                    !message.imageGenerationStatus ? (
                                    <div className="typing-indicator">
                                      <span className="typing-dot"></span>
                                      <span className="typing-dot"></span>
                                      <span className="typing-dot"></span>
                                    </div>
                                  ) : null}
                                  {/* Show image if completed - display BEFORE text content */}
                                  {message.imageGenerationStatus ===
                                    "completed" &&
                                    message.generatedImage && (
                                      <div className="generated-image-container">
                                        {message.revisedPrompt && (
                                          <div className="image-prompt">
                                            <div className="image-prompt-header">
                                              <span className="image-prompt-label">
                                                Revised Prompt:
                                              </span>
                                              <button
                                                className="image-prompt-toggle"
                                                onClick={() => {
                                                  setExpandedPrompts((prev) => {
                                                    const newSet = new Set(
                                                      prev
                                                    );
                                                    if (
                                                      newSet.has(message.id)
                                                    ) {
                                                      newSet.delete(message.id);
                                                    } else {
                                                      newSet.add(message.id);
                                                    }
                                                    return newSet;
                                                  });
                                                }}
                                                aria-label={
                                                  expandedPrompts.has(
                                                    message.id
                                                  )
                                                    ? "Collapse prompt"
                                                    : "Expand prompt"
                                                }
                                              >
                                                {expandedPrompts.has(
                                                  message.id
                                                ) ? (
                                                  <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  >
                                                    <polyline points="18 15 12 9 6 15"></polyline>
                                                  </svg>
                                                ) : (
                                                  <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                  >
                                                    <polyline points="6 9 12 15 18 9"></polyline>
                                                  </svg>
                                                )}
                                              </button>
                                            </div>
                                            <div
                                              className={`image-prompt-content ${
                                                expandedPrompts.has(message.id)
                                                  ? "expanded"
                                                  : "collapsed"
                                              }`}
                                            >
                                              {expandedPrompts.has(
                                                message.id
                                              ) ? (
                                                message.revisedPrompt
                                              ) : (
                                                <>
                                                  {message.revisedPrompt
                                                    .length > 150
                                                    ? `${message.revisedPrompt.substring(
                                                        0,
                                                        150
                                                      )}...`
                                                    : message.revisedPrompt}
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        <div className="image-wrapper">
                                          <img
                                            src={message.generatedImage}
                                            alt="AI generated visual"
                                            className="generated-image"
                                            onError={(e) => {
                                              console.error(
                                                "*** FRONTEND: Image failed to load ***",
                                                {
                                                  src: message.generatedImage?.substring(
                                                    0,
                                                    100
                                                  ),
                                                  hasImage:
                                                    !!message.generatedImage,
                                                  imageLength:
                                                    message.generatedImage
                                                      ?.length,
                                                }
                                              );
                                              e.target.style.display = "none";
                                            }}
                                            onLoad={() => {
                                              console.log(
                                                "*** FRONTEND: Image loaded successfully ***"
                                              );
                                            }}
                                          />
                                          <button
                                            className="download-image-button"
                                            onClick={async () => {
                                              try {
                                                // Fetch the image as a blob
                                                const response = await fetch(
                                                  message.generatedImage
                                                );
                                                const blob =
                                                  await response.blob();

                                                // Create a blob URL
                                                const blobUrl =
                                                  window.URL.createObjectURL(
                                                    blob
                                                  );

                                                // Create a temporary link and trigger download
                                                const link =
                                                  document.createElement("a");
                                                link.href = blobUrl;
                                                link.download = `generated-image-${Date.now()}.png`;
                                                link.style.display = "none";
                                                document.body.appendChild(link);
                                                link.click();

                                                // Clean up
                                                document.body.removeChild(link);
                                                window.URL.revokeObjectURL(
                                                  blobUrl
                                                );
                                              } catch (error) {
                                                console.error(
                                                  "Error downloading image:",
                                                  error
                                                );
                                                // Fallback: open in new tab if download fails
                                                window.open(
                                                  message.generatedImage,
                                                  "_blank"
                                                );
                                              }
                                            }}
                                            title="Download image"
                                          >
                                            <svg
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              className="download-icon"
                                            >
                                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                              <polyline points="7 10 12 15 17 10"></polyline>
                                              <line
                                                x1="12"
                                                y1="15"
                                                x2="12"
                                                y2="3"
                                              ></line>
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  {/* Show text content if present (can be shown alongside image) */}
                                  {message.content && (
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      components={{
                                        a: ({ node, children, ...props }) => {
                                          const href = props.href;
                                          // Handle files served by our backend
                                          if (
                                            href &&
                                            href.startsWith("/chatbot/files")
                                          ) {
                                            return (
                                              <a
                                                {...props}
                                                href={`${config.api.baseUrl}${href}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >{children}</a>
                                            );
                                          }
                                          // Handle OpenAI's internal sandbox/mnt paths (waiting for download)
                                          if (
                                            href &&
                                            (href.startsWith("sandbox:") ||
                                              href.startsWith("/mnt/data/") ||
                                              href.includes("/mnt/data/"))
                                          ) {
                                            return (
                                              <span
                                                className="file-pending-download"
                                                title="Preparing download..."
                                              >
                                                <span className="file-loading-spinner"></span>
                                                <span className="file-pending-text">
                                                  {children ||
                                                    "Preparing file..."}
                                                </span>
                                              </span>
                                            );
                                          }
                                          // Default external links
                                          return (
                                            <a
                                              {...props}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                            >{children}</a>
                                          );
                                        },
                                        code({
                                          node,
                                          inline,
                                          className,
                                          children,
                                          ...props
                                        }) {
                                          const match = /language-(\w+)/.exec(
                                            className || ""
                                          );
                                          return !inline && match ? (
                                            <StyledCodeBlock
                                              language={match[1]}
                                            >
                                              {children}
                                            </StyledCodeBlock>
                                          ) : (
                                            <code
                                              className={className}
                                              {...props}
                                            >
                                              {children}
                                            </code>
                                          );
                                        },
                                      }}
                                    >
                                      {message.content}
                                    </ReactMarkdown>
                                  )}
                                </>
                              ) : editingMessageId === message.id ? (
                                <textarea
                                  className="edit-message-textarea"
                                  value={editedContent}
                                  onChange={(e) =>
                                    setEditedContent(e.target.value)
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      handleSaveEdit(message.id);
                                    } else if (e.key === "Escape") {
                                      handleCancelEdit();
                                    }
                                  }}
                                  autoFocus
                                  rows={Math.min(
                                    Math.max(
                                      editedContent.split("\n").length,
                                      1
                                    ),
                                    10
                                  )}
                                />
                              ) : (
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    a: ({ node, children, ...props }) => {
                                      const href = props.href;
                                      // Handle files served by our backend
                                      if (
                                        href &&
                                        href.startsWith("/chatbot/files")
                                      ) {
                                        const fullUrl = `${config.api.baseUrl}${href}`;
                                        return (
                                          <a
                                            {...props}
                                            href={fullUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >{children}</a>
                                        );
                                      }
                                      // Handle OpenAI's internal sandbox/mnt paths (waiting for download)
                                      if (
                                        href &&
                                        (href.startsWith("sandbox:") ||
                                          href.startsWith("/mnt/data/") ||
                                          href.includes("/mnt/data/"))
                                      ) {
                                        return (
                                          <span
                                            className="file-pending-download"
                                            title="Preparing download..."
                                          >
                                            <span className="file-loading-spinner"></span>
                                            <span className="file-pending-text">
                                              {children ||
                                                "Preparing file..."}
                                            </span>
                                          </span>
                                        );
                                      }
                                      // Default external links
                                      return (
                                        <a
                                          {...props}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >{children}</a>
                                      );
                                    },
                                    code({
                                      node,
                                      inline,
                                      className,
                                      children,
                                      ...props
                                    }) {
                                      const match = /language-(\w+)/.exec(
                                        className || ""
                                      );
                                      return !inline && match ? (
                                        <StyledCodeBlock language={match[1]}>
                                          {children}
                                        </StyledCodeBlock>
                                      ) : (
                                        <code className={className} {...props}>
                                          {children}
                                        </code>
                                      );
                                    },
                                  }}
                                >
                                  {message.content || ""}
                                </ReactMarkdown>
                              )}
                            </div>
                            <div className="message-footer">
                              <div className="message-timestamp">
                                {formatMessageTime(message)}
                              </div>
                              <div className="message-actions">
                                {message.role === "user" &&
                                  message.content &&
                                  !editingMessageId && (
                                    <button
                                      className="edit-message-button"
                                      onClick={() =>
                                        handleEditMessage(
                                          message.id,
                                          message.content
                                        )
                                      }
                                      title="Edit message"
                                    >
                                      <svg
                                        className="edit-icon"
                                        viewBox="0 0 512 512"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="currentColor"
                                      >
                                        <path d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1 377.9 88 407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9 390.1 168 255.8 302.2c-2.9 2.9-6.5 5-10.4 6.1l-58.5 16.7 16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25C446.8-3.1 401.2-3.1 373.1 25zM88 64C39.4 64 0 103.4 0 152L0 424c0 48.6 39.4 88 88 88l272 0c48.6 0 88-39.4 88-88l0-112c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 112c0 22.1-17.9 40-40 40L88 464c-22.1 0-40-17.9-40-40l0-272c0-22.1 17.9-40 40-40l112 0c13.3 0 24-10.7 24-24s-10.7-24-24-24L88 64z" />
                                      </svg>
                                    </button>
                                  )}
                                {message.content && (
                                  <div className="copy-button-wrapper">
                                    <button
                                      className="copy-message-button"
                                      onClick={() =>
                                        handleCopyMessage(
                                          message.content,
                                          message.id
                                        )
                                      }
                                      title="Copy message"
                                    >
                                      <svg
                                        className="copy-icon"
                                        viewBox="0 0 448 512"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="currentColor"
                                      >
                                        <path d="M384 336l-192 0c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l140.1 0L400 115.9 400 320c0 8.8-7.2 16-16 16zM192 384l192 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L192 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l192 0c35.3 0 64-28.7 64-64l0-32-48 0 0 32c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l32 0 0-48-32 0z" />
                                      </svg>
                                    </button>
                                    {copiedMessageId === message.id && (
                                      <div className="copy-confirmation-popup">
                                        Copied!
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Attached Files Preview */}
                {attachmentPreviews.length > 0 && (
                  <div className="attached-files-container">
                    {attachmentPreviews.map((preview, index) => (
                      <div key={index} className="file-card">
                        {preview.file.type.startsWith("image/") ? (
                          <img
                            src={preview.url}
                            alt={preview.file.name}
                            className="file-preview-image"
                          />
                        ) : null}
                        <div className="file-details">
                          <span className="file-name" title={preview.file.name}>
                            {preview.file.name}
                          </span>
                          <span className="file-size">
                            {formatFileSize(preview.file.size)}
                          </span>
                        </div>
                        <button
                          className="remove-file-button"
                          onClick={() => handleRemoveFile(index)}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="chat-input-wrapper">
                  {chatError && (
                    <div className="chat-error-overlay">{chatError}</div>
                  )}
                  <button
                    className="back-button"
                    onClick={() => navigate('/synthetic-audience-workshop')}
                  >
                    ← Back
                  </button>
                  <div className="chat-input-container">
                    <input
                      type="file"
                      multiple
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />
                    <div className="tiptap-wrapper">
                      <EditorContent editor={editor} />
                    </div>
                    <div className="buttons-container">
                      <button
                        className="circular-button plus-button"
                        disabled={!isConnected}
                        onClick={() => fileInputRef.current.click()}
                      >
                        <img src={plusIcon} alt="Add" className="plus-icon" />
                      </button>
                      {/* Personas don't support prompt improvement or web search */}
                      {isResponding ? (
                        <button
                          className="circular-button stop-button"
                          onClick={handleStopResponse}
                          title="Stop response"
                        >
                          <div className="stop-icon"></div>
                        </button>
                      ) : (
                        <button
                          className="circular-button send-button"
                          disabled={
                            !isConnected ||
                            (!editorHasContent && attachedFiles.length === 0)
                          }
                          onClick={handleSendMessage}
                        >
                          <img src={arrow} alt="Send" className="send-icon" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PersonaChat;
